import React, { useCallback } from 'react';
import { useI18n } from '@monorepo/i18n';
import { ApiToken } from '@spilca/accounting_ts/v1/auth_web/auth_service_pb';
import { Button } from '@monorepo/ui-kit';
import { useNotifications } from '@monorepo/notification';
import styles from './index.module.css';

import useGetApiTokens from '../../hooks/useGetApiTokens';
import useCreateApiToken from '../../hooks/useCreateApiToken';

type ExpTs = { seconds?: number; nanos?: number };

const formatExpiration = (exp?: ExpTs) => {
  if (!exp || typeof exp.seconds !== 'number') return '—';
  const millis = exp.seconds * 1000 + (exp.nanos ?? 0) / 1_000_000;
  return new Date(millis).toLocaleString();
};

const ApiTokens: React.FC = () => {
  const { t } = useI18n();
  const { successNotification } = useNotifications();

  const { data: apiTokens, isLoading, error, refetch } = useGetApiTokens();
  const { mutate } = useCreateApiToken();
  const handleCopy = useCallback(
    (value: string) => {
      navigator.clipboard.writeText(value);
      successNotification(t('api.tokens.copied'));
    },
    [successNotification, t]
  );
  const handleGenerate = useCallback(() => {
    // If your RPC needs variables, pass them: mutate({ supplierId, expirationDate })
    mutate(undefined, {
      onSuccess: async () => {
        await refetch(); // refresh list
      }
    });
  }, [mutate, refetch]);

  // Loading
  if (isLoading || !apiTokens) {
    return <div>Loading...</div>;
  }
  if (error) {
    return (
      <div className={styles.empty}>
        {t('api.tokens.load_error')}
        <Button className={styles.addButton} onClick={() => refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const tokens = apiTokens.tokensList ?? [];

  // Empty
  if (tokens.length === 0) {
    return (
      <div className={styles.empty}>
        {t('api.tokens.empty')}{' '}
        <Button
          type="button"
          className={styles.addButton}
          onClick={handleGenerate}
        >
          {t('api.tokens.generate')}
        </Button>
      </div>
    );
  }

  // List
  return (
    <div className={styles.defaultBlock}>
      {tokens
        .filter((it: ApiToken.AsObject) => it.supplierId) // ignore placeholder rows
        .map((it: ApiToken.AsObject) => (
          <div key={it.token} className={styles.token}>
            <div
              className={styles.copy}
              onClick={() => handleCopy(it.token)}
              role="button"
              tabIndex={0}
            >
              {t('button.copy')}
            </div>
            <div className={styles.value}>{it.token}</div>
            <div className={styles.expirationDate}>
              <div className={styles.label}>{t('api.tokens.valid')}</div>
              <div className={styles.time}>
                {formatExpiration(it.expirationDate)}
              </div>
            </div>
          </div>
        ))}

      <Button
        type="button"
        className={styles.addButton}
        onClick={handleGenerate}
      >
        {t('api.tokens.generate')}
      </Button>
    </div>
  );
};

export default ApiTokens;
